import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
    CheckCircle2,
    Circle,
    Upload,
    File as FileIcon,
    AlertTriangle,
    Phone,
    Landmark,
    BookOpen,
    UserCircle,
    Send,
    LogOut,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EkgLine from "@/components/EkgLine";

const EMPLOYEE_DOCS = [
    { type: "w4", label: "W-4 (Federal Tax Withholding)" },
    { type: "i9", label: "I-9 (Employment Eligibility + ID)" },
    { type: "direct_deposit", label: "Voided Check / Direct Deposit Form" },
];
const CONTRACTOR_DOCS = [
    { type: "w9", label: "W-9 (Taxpayer Info)" },
    { type: "insurance", label: "Certificate of Insurance", needsExpiration: true },
];

export default function OnboardingWizard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [step, setStep] = useState(0);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showHandbookFull, setShowHandbookFull] = useState(false);

    const isContractor = user?.role === "contractor";
    const docList = isContractor ? CONTRACTOR_DOCS : EMPLOYEE_DOCS;

    const steps = useMemo(
        () =>
            [
                { key: "personal", label: "Personal info", icon: UserCircle },
                { key: "documents", label: "Documents", icon: FileIcon },
                { key: "emergency", label: "Emergency contact", icon: Phone },
                ...(isContractor
                    ? []
                    : [{ key: "direct_deposit", label: "Direct deposit", icon: Landmark }]),
                { key: "handbook", label: "Handbook", icon: BookOpen },
                { key: "review", label: "Submit", icon: Send },
            ],
        [isContractor],
    );

    const load = async () => {
        try {
            const { data } = await api.get("/onboarding/status");
            setData(data);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Already complete → redirect home
    useEffect(() => {
        if (data?.onboarding_status === "complete") {
            navigate("/", { replace: true });
        }
    }, [data?.onboarding_status, navigate]);

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center text-muted">
                Loading…
            </div>
        );
    }

    const summary = data.summary;
    const submitDisabled = !summary.can_submit_for_review;
    const currentKey = steps[step].key;

    const submitForReview = async () => {
        setBusy(true);
        setErr("");
        try {
            await api.post("/onboarding/submit");
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen" style={{ background: "var(--brand-cream)" }}>
            <div
                className="border-b border-[var(--brand-surface)] bg-[var(--brand-white)] sticky top-0 z-10"
                data-testid="onboarding-header"
            >
                <div
                    className="mx-auto flex items-center justify-between px-5 py-3 gap-3"
                    style={{ maxWidth: 1100 }}
                >
                    <div className="flex items-center gap-3">
                        <img src="/brand/logo-256.png" alt="CPR Paint" style={{ height: 32 }} />
                        <p
                            className="font-display font-bold"
                            style={{ color: "var(--brand-dark)" }}
                        >
                            Onboarding
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted">
                            {user?.name || user?.email}
                        </span>
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={async () => {
                                await logout();
                                navigate("/login", { replace: true });
                            }}
                            data-testid="onboarding-logout"
                        >
                            <LogOut size={14} />
                            Sign out
                        </button>
                    </div>
                </div>
                <EkgLine />
            </div>

            <div
                className="mx-auto"
                style={{ maxWidth: 1100, padding: "2rem 1.25rem 4rem" }}
            >
                <header className="fade-up">
                    <p className="eyebrow">Welcome to the crew</p>
                    <h1 className="h1 mt-3" data-testid="onboarding-title">
                        Let's get you ready for the field.
                    </h1>
                    <div
                        className="flex items-center gap-3 mt-4"
                        data-testid="onboarding-progress"
                    >
                        <div
                            className="flex-1 h-2 rounded-full overflow-hidden"
                            style={{ background: "var(--brand-surface)" }}
                        >
                            <div
                                className="h-full transition-all"
                                style={{
                                    width: `${summary.percent}%`,
                                    background: "var(--brand-red)",
                                }}
                            />
                        </div>
                        <span className="text-sm font-display font-bold">
                            {summary.percent}%
                        </span>
                    </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 mt-8">
                    {/* Stepper */}
                    <aside className="flex flex-col gap-1 brand-card" data-testid="onboarding-stepper">
                        {steps.map((s, i) => {
                            const stepDone = summary.steps.find((x) => x.key === s.key)?.complete;
                            const Icon = s.icon;
                            const isCurrent = i === step;
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => setStep(i)}
                                    className="sidebar-link"
                                    style={
                                        isCurrent
                                            ? {
                                                  color: "var(--brand-red)",
                                                  background: "rgba(210,5,28,0.07)",
                                              }
                                            : {}
                                    }
                                    data-testid={`onboarding-step-${s.key}`}
                                >
                                    {stepDone ? (
                                        <CheckCircle2
                                            size={18}
                                            style={{ color: "var(--brand-success)" }}
                                        />
                                    ) : (
                                        <Circle size={18} />
                                    )}
                                    <Icon size={14} className="ml-1" />
                                    <span>{s.label}</span>
                                </button>
                            );
                        })}
                    </aside>

                    <div className="brand-card fade-up" data-testid={`onboarding-panel-${currentKey}`}>
                        {err && (
                            <div
                                className="field-error mb-4"
                                data-testid="onboarding-error"
                            >
                                {err}
                            </div>
                        )}

                        {currentKey === "personal" && (
                            <PersonalStep data={data} onSaved={load} setErr={setErr} />
                        )}
                        {currentKey === "documents" && (
                            <DocumentsStep
                                data={data}
                                docList={docList}
                                onSaved={load}
                                setErr={setErr}
                            />
                        )}
                        {currentKey === "emergency" && (
                            <EmergencyStep data={data} onSaved={load} setErr={setErr} />
                        )}
                        {currentKey === "direct_deposit" && !isContractor && (
                            <DirectDepositStep data={data} onSaved={load} setErr={setErr} />
                        )}
                        {currentKey === "handbook" && (
                            <HandbookStep
                                data={data}
                                onSaved={load}
                                setErr={setErr}
                                showFull={showHandbookFull}
                                setShowFull={setShowHandbookFull}
                            />
                        )}
                        {currentKey === "review" && (
                            <ReviewStep
                                data={data}
                                steps={summary.steps}
                                onSubmit={submitForReview}
                                busy={busy}
                                disabled={submitDisabled}
                            />
                        )}

                        <div className="mt-8 flex items-center justify-between gap-2 pt-4 border-t border-[var(--brand-surface)]">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setStep((s) => Math.max(0, s - 1))}
                                disabled={step === 0}
                                data-testid="onboarding-prev"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                                disabled={step === steps.length - 1}
                                data-testid="onboarding-next"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PersonalStep({ data, onSaved, setErr }) {
    const [name, setName] = useState(data.name || "");
    const [phone, setPhone] = useState(data.phone || "");
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e?.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.put("/onboarding/personal", { name: name.trim(), phone: phone.trim() });
            await onSaved();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={save}>
            <p className="eyebrow">Step 1</p>
            <h2 className="h2 mt-2">Confirm your details</h2>
            <p className="text-muted mt-2 text-sm">
                Email is locked — ask your admin if it needs to change.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                    <label className="field-label">Full name</label>
                    <input
                        className="field-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        data-testid="op-name"
                    />
                </div>
                <div>
                    <label className="field-label">Email</label>
                    <input className="field-input" value={data.email} disabled />
                </div>
                <div>
                    <label className="field-label">Phone</label>
                    <input
                        className="field-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        data-testid="op-phone"
                    />
                </div>
                <div>
                    <label className="field-label">Role</label>
                    <input
                        className="field-input capitalize"
                        value={data.role}
                        disabled
                    />
                </div>
            </div>
            <button
                type="submit"
                className="btn btn-primary mt-5"
                disabled={saving}
                data-testid="op-save"
            >
                {saving ? "Saving…" : "Save"}
            </button>
        </form>
    );
}

function DocumentsStep({ data, docList, onSaved, setErr }) {
    const fileRef = useRef({});
    const [uploading, setUploading] = useState("");
    const docMap = Object.fromEntries((data.documents || []).map((d) => [d.type, d]));

    const upload = async (type, file, expiresAt) => {
        const form = new FormData();
        form.append("file", file);
        form.append("doc_type", type);
        if (expiresAt) form.append("expires_at", new Date(expiresAt).toISOString());
        setUploading(type);
        setErr("");
        try {
            await api.post("/onboarding/documents", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await onSaved();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setUploading("");
        }
    };

    return (
        <div>
            <p className="eyebrow">Step 2</p>
            <h2 className="h2 mt-2">Upload your forms</h2>
            <p className="text-muted mt-2 text-sm">
                PDF, JPG, or PNG up to 10 MB. We'll review each one before approving.
            </p>

            <ul className="mt-5 flex flex-col gap-3">
                {docList.map((dt) => {
                    const existing = docMap[dt.type];
                    return (
                        <li
                            key={dt.type}
                            className="border border-[var(--brand-surface)] rounded-md p-4"
                            data-testid={`op-doc-${dt.type}`}
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="font-display font-bold">{dt.label}</p>
                                    {existing ? (
                                        <p className="text-xs text-muted mt-1">
                                            <FileIcon size={12} className="inline" /> {existing.filename}{" "}
                                            {existing.verified && (
                                                <span className="badge-pill badge-success ml-2">
                                                    verified
                                                </span>
                                            )}
                                            {existing.rejected && (
                                                <span className="badge-pill badge-error ml-2">
                                                    rejected
                                                </span>
                                            )}
                                            {!existing.verified && !existing.rejected && (
                                                <span className="badge-pill badge-warning ml-2">
                                                    pending review
                                                </span>
                                            )}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-muted mt-1">Not uploaded yet.</p>
                                    )}
                                    {existing?.rejection_reason && (
                                        <p
                                            className="mt-2 text-xs flex items-start gap-1"
                                            style={{ color: "var(--brand-red)" }}
                                        >
                                            <AlertTriangle size={12} className="mt-0.5" />
                                            {existing.rejection_reason}
                                        </p>
                                    )}
                                </div>
                                <DocUploadButton
                                    docType={dt.type}
                                    needsExpiration={dt.needsExpiration}
                                    busy={uploading === dt.type}
                                    onPick={(file, exp) => upload(dt.type, file, exp)}
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function DocUploadButton({ docType, needsExpiration, busy, onPick }) {
    const inputRef = useRef();
    const [expires, setExpires] = useState("");
    return (
        <div className="flex items-center gap-2">
            {needsExpiration && (
                <input
                    type="date"
                    value={expires}
                    onChange={(e) => setExpires(e.target.value)}
                    className="field-input"
                    style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem", width: 150 }}
                    title="Expiration date"
                    data-testid={`op-doc-expires-${docType}`}
                />
            )}
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                data-testid={`op-doc-file-${docType}`}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPick(f, expires || null);
                    e.target.value = "";
                }}
            />
            <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || (needsExpiration && !expires)}
                onClick={() => inputRef.current?.click()}
                data-testid={`op-doc-upload-${docType}`}
            >
                <Upload size={14} />
                {busy ? "Uploading…" : "Upload"}
            </button>
        </div>
    );
}

function EmergencyStep({ data, onSaved, setErr }) {
    const ec = data.emergency_contact || {};
    const [form, setForm] = useState({
        name: ec.name || "",
        relationship: ec.relationship || "",
        phone: ec.phone || "",
    });
    const [saving, setSaving] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.put("/onboarding/emergency-contact", form);
            await onSaved();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };
    return (
        <form onSubmit={save}>
            <p className="eyebrow">Step 3</p>
            <h2 className="h2 mt-2">Emergency contact</h2>
            <p className="text-muted mt-2 text-sm">
                Someone we can reach if there's an incident on a job site.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                    <label className="field-label">Name</label>
                    <input
                        className="field-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        data-testid="op-ec-name"
                    />
                </div>
                <div>
                    <label className="field-label">Relationship</label>
                    <input
                        className="field-input"
                        value={form.relationship}
                        onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                        required
                        placeholder="Spouse, parent, friend…"
                        data-testid="op-ec-rel"
                    />
                </div>
                <div className="sm:col-span-2">
                    <label className="field-label">Phone</label>
                    <input
                        className="field-input"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        required
                        data-testid="op-ec-phone"
                    />
                </div>
            </div>
            <button
                type="submit"
                className="btn btn-primary mt-5"
                disabled={saving}
                data-testid="op-ec-save"
            >
                {saving ? "Saving…" : "Save"}
            </button>
        </form>
    );
}

function DirectDepositStep({ data, onSaved, setErr }) {
    const dd = data.direct_deposit || {};
    const [form, setForm] = useState({
        bank_name: dd.bank_name || "",
        account_type: dd.account_type || "checking",
        routing_number: "",
        account_number: "",
    });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.put("/onboarding/direct-deposit", form);
            setForm((f) => ({ ...f, routing_number: "", account_number: "" }));
            await onSaved();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={save}>
            <p className="eyebrow">Step 4</p>
            <h2 className="h2 mt-2">Direct deposit</h2>
            <p className="text-muted mt-2 text-sm">
                Encrypted at rest. Once saved, only the last 4 digits will be visible.
            </p>
            {dd?.routing_number_masked && (
                <div
                    className="brand-card mt-4"
                    style={{ background: "var(--brand-cream)" }}
                >
                    <p className="text-xs text-muted">Current on file</p>
                    <p className="font-display font-bold">{dd.bank_name}</p>
                    <p className="text-sm">
                        <span className="capitalize">{dd.account_type}</span> · Routing{" "}
                        {dd.routing_number_masked} · Account {dd.account_number_masked}
                    </p>
                    <p className="text-xs text-muted mt-1">
                        Re-submit below if you need to update.
                    </p>
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                    <label className="field-label">Bank name</label>
                    <input
                        className="field-input"
                        value={form.bank_name}
                        onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                        required
                        data-testid="op-dd-bank"
                    />
                </div>
                <div>
                    <label className="field-label">Account type</label>
                    <select
                        className="field-select"
                        value={form.account_type}
                        onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                        data-testid="op-dd-type"
                    >
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                    </select>
                </div>
                <div>
                    <label className="field-label">Routing number (9 digits)</label>
                    <input
                        className="field-input"
                        value={form.routing_number}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                routing_number: e.target.value.replace(/\D/g, "").slice(0, 9),
                            })
                        }
                        required={!dd?.routing_number_masked}
                        placeholder="123456789"
                        data-testid="op-dd-routing"
                    />
                </div>
                <div>
                    <label className="field-label">Account number</label>
                    <input
                        className="field-input"
                        value={form.account_number}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                account_number: e.target.value.replace(/\D/g, "").slice(0, 32),
                            })
                        }
                        required={!dd?.routing_number_masked}
                        data-testid="op-dd-account"
                    />
                </div>
            </div>
            <button
                type="submit"
                className="btn btn-primary mt-5"
                disabled={saving}
                data-testid="op-dd-save"
            >
                {saving ? "Saving…" : "Save"}
            </button>
        </form>
    );
}

function HandbookStep({ data, onSaved, setErr, showFull, setShowFull }) {
    const [handbook, setHandbook] = useState(null);
    const [signature, setSignature] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/onboarding/handbook")
            .then(({ data }) => setHandbook(data))
            .catch((e) => setErr(apiErrorMessage(e)));
    }, [setErr]);

    const ack = async (e) => {
        e.preventDefault();
        if (!signature.trim()) return;
        setSaving(true);
        setErr("");
        try {
            await api.post("/onboarding/handbook/acknowledge", { signature: signature.trim() });
            await onSaved();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };

    if (!handbook) return <p className="text-muted">Loading handbook…</p>;

    return (
        <div>
            <p className="eyebrow">Step</p>
            <h2 className="h2 mt-2">{handbook.title}</h2>
            <p className="text-muted mt-2 text-sm">Version {handbook.version}</p>

            <div
                className={`mt-5 rounded-md border border-[var(--brand-surface)] p-5 ${
                    showFull ? "" : "relative"
                }`}
                style={{
                    background: "var(--brand-cream)",
                    maxHeight: showFull ? "none" : 320,
                    overflow: "hidden",
                }}
                data-testid="op-handbook-body"
            >
                <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>{handbook.content}</ReactMarkdown>
                </div>
                {!showFull && (
                    <div
                        style={{
                            position: "absolute",
                            inset: "60% 0 0 0",
                            background:
                                "linear-gradient(to bottom, rgba(247,245,239,0), var(--brand-cream))",
                        }}
                    />
                )}
            </div>
            <button
                type="button"
                className="btn btn-ghost mt-2"
                onClick={() => setShowFull((v) => !v)}
                data-testid="op-handbook-toggle"
            >
                {showFull ? "Collapse" : "Read full handbook"}
            </button>

            {data.handbook_acknowledged ? (
                <div
                    className="brand-card mt-5"
                    style={{
                        background: "rgba(46,125,50,0.06)",
                        borderColor: "rgba(46,125,50,0.4)",
                    }}
                    data-testid="op-handbook-acked"
                >
                    <CheckCircle2
                        size={18}
                        style={{ color: "var(--brand-success)" }}
                        className="inline mr-2"
                    />
                    Acknowledged on{" "}
                    {data.handbook_acknowledged_at &&
                        new Date(data.handbook_acknowledged_at).toLocaleString()}{" "}
                    (v{data.handbook_version})
                </div>
            ) : (
                <form className="mt-5" onSubmit={ack}>
                    <label className="field-label">Type your full name to acknowledge</label>
                    <input
                        className="field-input"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        required
                        placeholder="Sam Crew"
                        data-testid="op-handbook-signature"
                    />
                    <button
                        type="submit"
                        className="btn btn-primary mt-3"
                        disabled={saving}
                        data-testid="op-handbook-ack"
                    >
                        I have read and agree
                    </button>
                </form>
            )}
        </div>
    );
}

function ReviewStep({ data, steps, onSubmit, busy, disabled }) {
    if (data.onboarding_status === "pending_review") {
        return (
            <div
                className="brand-card"
                style={{
                    background: "rgba(46,125,50,0.06)",
                    borderColor: "rgba(46,125,50,0.4)",
                }}
                data-testid="op-pending-review"
            >
                <CheckCircle2 size={28} style={{ color: "var(--brand-success)" }} />
                <h2 className="h2 mt-3">You're all set</h2>
                <p className="text-muted mt-2">
                    Your onboarding is in your admin's queue. We'll email you the moment everything
                    is approved.
                </p>
            </div>
        );
    }
    return (
        <div data-testid="op-review">
            <p className="eyebrow">Final</p>
            <h2 className="h2 mt-2">Ready to submit?</h2>
            <p className="text-muted mt-2 text-sm">
                Once you submit, your admin can review and approve. You can still update
                anything before then.
            </p>
            <ul className="mt-5 flex flex-col gap-2">
                {steps.map((s) => (
                    <li key={s.key} className="flex items-center gap-3 text-sm">
                        {s.complete ? (
                            <CheckCircle2 size={18} style={{ color: "var(--brand-success)" }} />
                        ) : (
                            <Circle size={18} style={{ color: "var(--brand-muted)" }} />
                        )}
                        <span className={s.complete ? "" : "text-muted"}>{s.label}</span>
                    </li>
                ))}
            </ul>
            <button
                type="button"
                className="btn btn-primary mt-6"
                disabled={disabled || busy}
                onClick={onSubmit}
                data-testid="op-submit"
            >
                <Send size={14} />
                Submit for review
            </button>
            {disabled && (
                <p className="text-xs text-muted mt-3">
                    Finish the remaining steps in the sidebar to enable submit.
                </p>
            )}
        </div>
    );
}
